import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useRefreshCatalogue } from "../../apis/useGetCatalogueData";
import LandingPageCoverSrc from "../../assets/images/LandingPageCover.svg?url";
import TouchToStartIcon from "../../assets/images/TouchToStart.png";
import { APP_MODE } from "../../common/envconst";
import I18nButton from "../../components/I18nButton/I18nButton";
import SOKLogo from "../../components/Logo/SOKLogo";
import PinAuthModal from "../../components/PinAuthModal/PinAuthModal";
import {
	getVersionString,
	shouldRenderVersion,
} from "../../components/VersionDisplay/VersionDisplay";
import { useCartActions } from "../../contexts/CartServiceContext/CartServiceContext";
import { useConfigActions, useConfigContext } from "../../contexts/ConfigContext/ConfigContext";
import { usePaymentActions } from "../../contexts/PaymentContext/PaymentContext";
import { PATH } from "../../router/path";
import { addDatadogAction, buildLocationContext } from "../../services/datadog";
import "./LandingPage.scss";

const ADMIN_TAP_THRESHOLD = 10;
const ADMIN_TAP_WINDOW_MS = 10000;

const LandingPage = () => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { GRANT_MAINTENANCE_ACCESS, GRANT_ADMIN_ACCESS } = useConfigActions();
	const { state: enableDevTools } = useConfigContext((state) => state.config.enableDevTools);
	const { state: location } = useConfigContext((state) => state.location);
	const { state: stall } = useConfigContext((state) => state.config.stall);
	const { CLEAN_CART } = useCartActions();
	const { CLEAR_PAYMENT_DATA } = usePaymentActions();
	const refreshCatalogue = useRefreshCatalogue();

	// ABCD sequence tracking
	const sequenceRef = useRef<string[]>([]);
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);
	const expectedSequence = ["A", "B", "C", "D"];

	// Admin tap counter (top-right corner = version button area)
	const adminTapCountRef = useRef(0);
	const adminTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	const [isPinModalOpen, setIsPinModalOpen] = useState(false);
	const [pinModalKey, setPinModalKey] = useState(0);

	useEffect(() => {
		CLEAN_CART();
		CLEAR_PAYMENT_DATA();
		// Re-fetch catalogue on every entry to LANDING (manual / auto startover, app boot).
		// SWR dedupingInterval (2s default) coalesces this with ApiHandler's first-mount
		// fetch on initial app load, so it only doubles up when the user actually resets.
		refreshCatalogue();
	}, [CLEAN_CART, CLEAR_PAYMENT_DATA, refreshCatalogue]);

	useEffect(() => {
		return () => {
			if (adminTimeoutRef.current) {
				clearTimeout(adminTimeoutRef.current);
			}
		};
	}, []);

	const handleStartOrdering = () => {
		addDatadogAction("landing_start_ordering", {
			...buildLocationContext(location, stall),
		});
		navigate(PATH.PRODUCT_LIST);
	};

	const handlePinSuccess = useCallback(() => {
		addDatadogAction("landing_pin_auth_success", {
			...buildLocationContext(location, stall),
		});
		setIsPinModalOpen(false);
		GRANT_MAINTENANCE_ACCESS();
		navigate(PATH.MAINTENANCE);
	}, [GRANT_MAINTENANCE_ACCESS, navigate, location, stall]);

	const handlePinFailure = useCallback(
		(tapsLength: number) => {
			addDatadogAction("landing_pin_auth_failed", {
				...buildLocationContext(location, stall),
				tapsLength,
			});
		},
		[location, stall]
	);

	const handlePinClose = useCallback(() => {
		setIsPinModalOpen(false);
	}, []);

	const handleCornerClick = useCallback(
		(button: string) => {
			sequenceRef.current.push(button);

			addDatadogAction("landing_corner_tap", {
				...buildLocationContext(location, stall),
				corner: button,
				sequenceLength: sequenceRef.current.length,
			});

			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
			timeoutRef.current = setTimeout(() => {
				sequenceRef.current = [];
			}, 10000);

			const currentSequence = sequenceRef.current;
			// Check the partial sequence against the expected prefix.
			const partialMatches = currentSequence.every(
				(letter, index) => letter === expectedSequence[index]
			);

			if (!partialMatches) {
				// Wrong key for this position — reset.
				sequenceRef.current = [];

				return;
			}

			if (currentSequence.length === expectedSequence.length) {
				// Full sequence completed correctly — open PIN auth modal.
				sequenceRef.current = [];
				if (timeoutRef.current) {
					clearTimeout(timeoutRef.current);
				}
				addDatadogAction("landing_pin_modal_opened", {
					...buildLocationContext(location, stall),
				});
				setPinModalKey((k) => k + 1);
				setIsPinModalOpen(true);
			}
		},
		[location, stall]
	);

	const handleAdminTap = useCallback(() => {
		if (!enableDevTools) return;
		adminTapCountRef.current += 1;

		addDatadogAction("landing_admin_unlock_progress", {
			...buildLocationContext(location, stall),
			count: adminTapCountRef.current,
			threshold: ADMIN_TAP_THRESHOLD,
		});

		if (adminTimeoutRef.current) {
			clearTimeout(adminTimeoutRef.current);
		}

		adminTimeoutRef.current = setTimeout(() => {
			adminTapCountRef.current = 0;
		}, ADMIN_TAP_WINDOW_MS);

		if (adminTapCountRef.current >= ADMIN_TAP_THRESHOLD) {
			adminTapCountRef.current = 0;
			if (adminTimeoutRef.current) {
				clearTimeout(adminTimeoutRef.current);
			}

			addDatadogAction("landing_admin_access_granted", {
				...buildLocationContext(location, stall),
			});
			GRANT_ADMIN_ACCESS();
			navigate(PATH.ADMIN);
		}
	}, [navigate, GRANT_ADMIN_ACCESS, enableDevTools, location, stall]);

	const handleTopRightClick = useCallback(() => {
		handleCornerClick("B");
		handleAdminTap();
	}, [handleCornerClick, handleAdminTap]);

	const versionString = getVersionString();
	const showVersion = shouldRenderVersion(APP_MODE, versionString);

	return (
		<div className="LandingPage">
			{/* Hidden corner buttons for ABCD sequence */}
			<button
				className="LandingPage__corner-btn LandingPage__corner-btn--top-left"
				onClick={() => handleCornerClick("A")}
				aria-label="Corner button A"
			/>
			<button
				className="LandingPage__corner-btn LandingPage__corner-btn--top-right"
				onClick={handleTopRightClick}
				aria-label="Corner button B"
			>
				{showVersion && (
					<span className="LandingPage__version" title={versionString}>
						{versionString}
					</span>
				)}
			</button>
			<button
				className="LandingPage__corner-btn LandingPage__corner-btn--bottom-right"
				onClick={() => handleCornerClick("C")}
				aria-label="Corner button C"
			/>
			<button
				className="LandingPage__corner-btn LandingPage__corner-btn--bottom-left"
				onClick={() => handleCornerClick("D")}
				aria-label="Corner button D"
			/>

			<div className="LandingPageContent">
				<div className="LandingPageBrandTitle">
					<SOKLogo />
				</div>

				<div className="LandingPageCover">
					<img src={LandingPageCoverSrc} alt="Landing Page Cover" draggable={false} />
				</div>

				<div className="LandingPageBottom">
					<div className="LandingPageLanguageButtons">
						<button className="LandingPageWelcomeButton" type="button">
							{t("LandingPage.welcome")}
						</button>
						<I18nButton className="LandingPageLanguageButton" />
					</div>

					<button className="LandingPageStartButton" onClick={handleStartOrdering} type="button">
						<img
							src={TouchToStartIcon}
							alt="Touch"
							className="LandingPageStartButtonIcon"
							draggable={false}
						/>
						{t("LandingPage.touchToStart")}
					</button>
				</div>
			</div>

			<PinAuthModal
				key={pinModalKey}
				open={isPinModalOpen}
				onSuccess={handlePinSuccess}
				onFailure={handlePinFailure}
				onClose={handlePinClose}
			/>
		</div>
	);
};

export default LandingPage;
